import React, { useCallback, useState } from 'react';
import useMergedRef from '@react-hook/merged-ref';
import type { PressableProps } from 'react-native';
import { Pressable, StyleSheet, View } from 'react-native';
import type { ListActionProps } from './ListAction';
import { ListAction } from './ListAction';
import { Stepper } from '../Stepper';
import { Switch } from '../Switch';
import { Icon } from '../Icon';
import { COLORS, SPACING } from '@garnish/constants';
import { CollapseIndicatorIcon, Collapsible } from '../Collapsible';
import { ResponsiveSelect } from '../ResponsiveSelect';
import { useBreakpointHelpers, usePressableState } from '../../hooks';
import type { TagAlert } from '../TagAlert';

// ────────────────────────────────────────────────────────────────────────────────

export const ListActionInfo = (props: ListActionInfoProps) => {
  const { title, notice, children, ...listActionProps } = props;

  return (
    <ListAction {...listActionProps}>
      <ListAction.Title>{title}</ListAction.Title>
      {children}
      <ListAction.Notice>{notice}</ListAction.Notice>
    </ListAction>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

export const ListActionPressable = React.forwardRef<
  View,
  ListActionInfoProps & PressableProps
>((props, ref) => {
  const {
    testID,
    title,
    notice,
    iconName,
    children,
    accessibilityLabel,
    accessibilityHint,
    onPress,
  } = props;
  const { isMobile } = useBreakpointHelpers();
  const pressableRef = React.useRef(null);
  const { isInteracting } = usePressableState(pressableRef);

  return (
    <Pressable
      ref={useMergedRef(pressableRef, ref)}
      testID={testID ?? `sg-list-action-pressable-${title}`}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? title}
      accessibilityHint={accessibilityHint ?? title}
      onPress={onPress}
    >
      <ListAction.Wrapper>
        <ListAction.Icon iconName={iconName} />
        <ListAction.Content>
          <ListAction.Title underline={isInteracting}>{title}</ListAction.Title>
          {children}
          <ListAction.Notice>{notice}</ListAction.Notice>
        </ListAction.Content>
        {isMobile ? (
          <Icon
            width={18}
            height={18}
            name="IconCaretRight"
            color={COLORS.NEUTRAL_2}
          />
        ) : null}
      </ListAction.Wrapper>
    </Pressable>
  );
});

// ────────────────────────────────────────────────────────────────────────────────

export const ListActionSwitch = (props: ListActionSwitchProps) => {
  const { iconName, title, text, notice, ...switchProps } = props;

  return (
    <ListAction iconName={iconName}>
      <View style={styles.templateContent}>
        <View style={styles.templateContentInner}>
          <ListAction.Title>{title}</ListAction.Title>
          <ListAction.Text>{text}</ListAction.Text>
          <ListAction.Notice>{notice}</ListAction.Notice>
        </View>

        <View>
          <Switch testID="sg-list-action-switch-control" {...switchProps} />
        </View>
      </View>
    </ListAction>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

export const ListActionStepper = (props: ListActionStepperProps) => {
  const { iconName, title, text, notice, ...stepperProps } = props;

  return (
    <ListAction iconName={iconName}>
      <View style={styles.templateContent}>
        <View style={styles.templateContentInner}>
          <ListAction.Title>{title}</ListAction.Title>
          <ListAction.Text>{text}</ListAction.Text>
          <ListAction.Notice>{notice}</ListAction.Notice>
        </View>

        <View>
          <Stepper {...stepperProps} />
        </View>
      </View>
    </ListAction>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

export const ListActionSelect = (props: ListActionSelectProps) => {
  const {
    iconName,
    title = 'Select option',
    modalTitle,
    notice,
    value,
    options,
    onSelect,
    testID,
    isLoading,
    noticePalette = 'neutral',
  } = props;

  return (
    <ListAction iconName={iconName}>
      <ResponsiveSelect
        testID={testID}
        value={value}
        label={title}
        modalTitle={modalTitle}
        options={options}
        onSelect={onSelect}
        isLoading={isLoading}
      />

      <ListAction.Notice palette={noticePalette}>{notice}</ListAction.Notice>
    </ListAction>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

export const ListActionCollapsible = (props: ListActionCollapsibleProps) => {
  const { title, text, notice, children, onChange, ...listActionProps } = props;
  const [expanded, setExpanded] = useState(false);
  const handleExpandedStateChange = useCallback(
    (expandedState: boolean) => {
      setExpanded(expandedState);
      onChange?.(expandedState);
    },
    [onChange],
  );

  return (
    <Collapsible
      options={{ hideIcon: true }}
      onChange={handleExpandedStateChange}
    >
      <Collapsible.Summary>
        <ListAction hasPressableStyles {...listActionProps}>
          <View style={styles.templateContent}>
            <View style={styles.templateContentInner}>
              <ListAction.Title>{title}</ListAction.Title>
              <ListAction.Text>{text}</ListAction.Text>
              <ListAction.Notice>{notice}</ListAction.Notice>
            </View>

            <CollapseIndicatorIcon expanded={expanded} />
          </View>
        </ListAction>
      </Collapsible.Summary>

      <Collapsible.Details>{children}</Collapsible.Details>
    </Collapsible>
  );
};

//
// ─── TYPES ──────────────────────────────────────────────────────────────────────
//

type ListActionTemplateProps = {
  readonly title?: string;
  readonly text?: string;
  readonly notice?: string;
  readonly noticePalette?: React.ComponentProps<typeof TagAlert>['palette'];
} & Pick<ListActionProps, 'iconName' | 'children'>;

type ListActionInfoProps = Omit<ListActionTemplateProps, 'text'>;

type ListActionSwitchProps = React.ComponentProps<typeof Switch> &
  ListActionTemplateProps;

type ListActionStepperProps = ListActionTemplateProps &
  React.ComponentProps<typeof Stepper>;

type ListActionSelectProps = Omit<ListActionTemplateProps, 'text'> &
  Omit<React.ComponentProps<typeof ResponsiveSelect>, 'label'>;

type ListActionCollapsibleProps = {
  readonly onChange?: (expanded: boolean) => void;
} & ListActionTemplateProps;

//
// ─── STYLES ─────────────────────────────────────────────────────────────────────
//

const styles = StyleSheet.create({
  templateContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  templateContentInner: {
    flex: 1,
    paddingRight: SPACING.small,
  },
});
